import { BoxProps, makeStyles, Menu, MenuItem } from '@material-ui/core';
import React, { useState } from 'react';

import { StyledButton } from '@/components/modules/Metrics/shared/components/StyledButton';
import { Icon } from '@/components/shared/components/Icon';
import { ItemSelectOption } from '@/components/shared/components/LastActionsSelectOptions/types/ItemSelectOption';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface StyledSelectorProps<T> extends BoxProps {
  selectOptions: ItemSelectOption<T>[];
  setSelectedValue: (option: T) => void;
  startIcon: string;
  currentSelectedValue?: string;
  noSelectionLabel?: string;
}

export const LastActionsSelector = <T,>({
  selectOptions = [],
  currentSelectedValue,
  setSelectedValue,
  noSelectionLabel = 'SELECT',
  startIcon,
}: StyledSelectorProps<T>) => {
  const { palette } = useCustomTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const useStyles = makeStyles({
    root: {
      '& .MuiList-root': {
        paddingTop: 0,
      },
      '& :hover.MuiListItem-root': {
        backgroundColor: palette.grey[100],
      },
    },
    button: {
      minWidth: '0 !important',
      '& .MuiButton-label': {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      },
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <StyledButton
        value={currentSelectedValue ?? noSelectionLabel}
        onClick={handleClick}
        startIcon={<Icon size={16}>{startIcon}</Icon>}
        endIcon={<Icon size={20}>expand_more</Icon>}
        classes={{ root: useStyles().button }}
      />
      <Menu
        className={useStyles().root}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        style={{ maxHeight: '460px' }}
      >
        {selectOptions.length === 0 ? (
          <MenuItem key={'no-options'} dense disabled>
            <Text tId={'NO_OPTIONS_TO_DISPLAY'} />
          </MenuItem>
        ) : (
          selectOptions.map((option, i) =>
            !option.value ? (
              option.display
            ) : (
              <MenuItem
                dense
                key={`${i}-${option.value}`}
                onClick={() => {
                  setSelectedValue(option.value as T);
                  handleClose();
                }}
              >
                {option.display}
              </MenuItem>
            ),
          )
        )}
      </Menu>
    </>
  );
};
